import {useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {buildUserFacingErrorLabels} from "../../../userFacingErrorUtils";
import {
    useEventBasicSetupQuery,
    useEventFeeTypesListQuery
} from "../../../GraphQL/generated/graphql";

// Route event id, error labels and the event-scoped list filter shared by every event-config page.
export const useEventConfigScope = () => {
    const {t} = useTranslation();
    const {eventId} = useParams();
    const eventNumericId = eventId ? Number(eventId) : undefined;
    const errorLabels = buildUserFacingErrorLabels(t);
    const baseWhere = useCallback(<T, >(extra?: T) => ({
        and: [
            ...(eventNumericId ? [{eventId: {eq: eventNumericId}} as any] : []),
            ...(extra ? [extra as any] : [])
        ]
    }), [eventNumericId]);

    return {eventNumericId, errorLabels, baseWhere};
};

export type EventConfigScope = ReturnType<typeof useEventConfigScope>;

// Event name, dates and participation-fee flag; used by basics, fees and accommodation.
export const useEventBasicSetup = (eventNumericId?: number) => {
    const query = useEventBasicSetupQuery({
        variables: eventNumericId ? {id: eventNumericId} : undefined,
        skip: !eventNumericId,
        fetchPolicy: "cache-and-network"
    });

    return {query, event: query.data?.eventById};
};

// Participant fee types; used by fees and activities.
export const useEventFeeTypesQuery = ({baseWhere}: EventConfigScope) =>
    useEventFeeTypesListQuery({variables: {take: 50, where: baseWhere()}});
